// frontend/src/components/forecastchart/ForecastChart.jsx
// Bar chart of one item's statistical forecast by month.

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import Chart from '../charts/Chart';
import { headroomMax } from '../charts/axisHeadroom';
import { useFormatters } from '../../hooks/useFormatters';
import './ForecastChart.css';

/**
 * @param {Object} props
 * @param {Array|null} props.statisticalForecast - Array of {date: 'YYYY-MM', value: number}; null when the stored forecast is unreadable
 * @param {number|string} props.height - Chart height (number in pixels or string like "100%", default: 400)
 */
function ForecastChart({
  statisticalForecast,
  height = 400
}) {
  const { t } = useTranslation();
  const { formatCompact, formatMonthYear } = useFormatters();

  const chartData = useMemo(
    () => (statisticalForecast === null
      ? []
      : [...statisticalForecast].sort((a, b) => a.date.localeCompare(b.date))),
    [statisticalForecast],
  );

  const chartOptions = useMemo(() => {
    let yDataMax = 0;
    for (const { value } of chartData) {
      if (typeof value === 'number' && Number.isFinite(value) && value > yDataMax) yDataMax = value;
    }
    const yMaxPadded = headroomMax(yDataMax);

    return {
      data: chartData,
      title: { text: t('forecast.statisticalOnly') },
      series: [
        {
          type: 'bar',
          xKey: 'date',
          yKey: 'value',
          yName: t('forecast.statisticalOnly'),
        },
      ],
      axes: {
        x: {
          type: 'category',
          label: {
            rotation: 45,
            formatter: ({ value }) => formatMonthYear(value),
          },
          paddingInner: 0.3,
        },
        y: {
          type: 'number',
          title: { text: t('forecast.forecastQuantity') },
          label: { formatter: params => formatCompact(params.value) },
          min: 0,
          ...(yMaxPadded != null ? { max: yMaxPadded } : {}),
          nice: true
        }
      },
      zoom: {
        enabled: false,
        enableScrolling: false,
        enableAxisDragging: false,
        enableAxisScrolling: false,
      },
      tooltip: { mode: 'shared' },
    };
  }, [chartData, t, formatCompact, formatMonthYear]);

  const heightStyle = typeof height === 'number' ? `${height}px` : height;

  if (chartData.length === 0) {
    return (
      <Box className="forecast-chart-no-data" sx={{ height: heightStyle }}>
        <Typography variant="body2" className="forecast-chart-no-data-text">
          {statisticalForecast === null ? t('charts.common.dataUnavailable') : t('forecast.noChartData')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="forecast-chart-container" sx={{ height: heightStyle }}>
      <Chart className="forecast-chart" options={chartOptions} />
    </Box>
  );
}

export default ForecastChart;
